import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, mediaUrl } from "@/lib/api";
import { enqueue, flushQueue, queueCount } from "@/lib/uploadQueue";
import { useAuth } from "@/context/AuthContext";
import { SeverityBadge, StatusBadge } from "@/components/Brand";
import {
  ChevronLeft, Camera, Video, Mic, MicOff, Sparkles, FileText, FileSpreadsheet,
  Mail, Plus, Trash2, Pencil, CircleDollarSign, MapPin, Loader2, Image as ImageIcon, UploadCloud,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CATEGORIES = ["Damage", "Missing Item", "Replace/Repair", "Cleaning", "Safety", "Renovation", "Other"];
const SEVERITIES = [
  { key: "minor", label: "Minor", ring: "ring-amber-400 bg-amber-50 text-amber-800" },
  { key: "moderate", label: "Moderate", ring: "ring-orange-400 bg-orange-50 text-orange-800" },
  { key: "major", label: "Major", ring: "ring-red-400 bg-red-50 text-red-800" },
];

function useDictation(onText) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const toggle = useCallback(async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "note.webm");
          const { data } = await api.post("/media/transcribe", fd);
          if (data.text) onText(data.text);
          else toast.info("No speech detected");
        } catch (err) {
          toast.error(apiErr(err));
        } finally {
          setBusy(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  }, [recording, onText]);

  return { recording, busy, toggle };
}

function FindingDialog({ open, onOpenChange, onSave, initial }) {
  const [f, setF] = useState(initial);
  useEffect(() => { setF(initial); }, [initial, open]);
  const dict = useDictation((t) => setF((p) => ({ ...p, remarks: (p.remarks ? p.remarks + " " : "") + t })));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{initial.id ? "Edit Finding" : "Add Finding"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Issue title</label>
            <input data-testid="finding-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
              placeholder="Water stain under kitchen sink" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="mb-1 flex items-center justify-between text-sm font-semibold text-slate-700">
              Remarks
              <button type="button" data-testid="finding-dictate-btn" onClick={dict.toggle}
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${dict.recording ? "bg-red-100 text-red-700 animate-pulse-subtle" : "bg-blue-50 text-blue-600"}`}>
                {dict.busy ? <Loader2 className="h-3 w-3 animate-spin" /> : dict.recording ? <MicOff className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
                {dict.busy ? "Transcribing" : dict.recording ? "Stop" : "Dictate"}
              </button>
            </label>
            <textarea data-testid="finding-remarks-input" rows={3} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })}
              placeholder="Cabinet base swollen, possible slow leak…" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Category</label>
            <select data-testid="finding-category-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Severity</label>
            <div className="grid grid-cols-3 gap-2">
              {SEVERITIES.map((s) => (
                <button key={s.key} type="button" data-testid={`finding-severity-${s.key}-chip`} onClick={() => setF({ ...f, severity: s.key })}
                  className={`rounded-lg py-3 text-sm font-bold uppercase tracking-wide ring-2 transition-all ${f.severity === s.key ? s.ring : "ring-slate-200 bg-white text-slate-500"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Location</label>
              <input data-testid="finding-location-input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })}
                placeholder="rear bedroom window" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Est. cost ($)</label>
              <input data-testid="finding-cost-input" type="number" min="0" value={f.cost_estimate ?? ""} onChange={(e) => setF({ ...f, cost_estimate: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="450" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Status</label>
            <select data-testid="finding-status-select" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600">
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="save-finding-btn" disabled={!f.title}
            onClick={() => onSave(f)} className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {initial.id ? "Update finding" : "Add finding"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FindingMedia({ f, media, canEdit, onAddBefore, onAddAfter, onDelete, onEdit, onEnlarge }) {
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const before = media.filter((m) => m.phase !== "after");
  const after = media.filter((m) => m.phase === "after");
  if (!canEdit && media.length === 0) return null;

  const Thumb = ({ m, tone }) => (
    <div className="group/thumb relative">
      <button type="button" onClick={() => onEnlarge(m)} className="block">
        <img data-testid={`${tone}-photo-${m.id}`} src={mediaUrl(m.id)} alt={m.caption || tone}
          className={`h-16 w-16 cursor-zoom-in rounded-md object-cover ring-2 ${tone === "after" ? "ring-emerald-400" : "ring-blue-300"}`} />
      </button>
      <span className={`pointer-events-none absolute left-1 top-1 rounded px-1 py-0.5 text-[8px] font-bold uppercase leading-none text-white ${tone === "after" ? "bg-emerald-500" : "bg-blue-500"}`}>{tone}</span>
      {canEdit && (
        <div className="absolute right-0.5 top-0.5 flex gap-0.5 opacity-100 transition sm:opacity-0 sm:group-hover/thumb:opacity-100">
          <button type="button" data-testid={`edit-finding-media-${m.id}`} onClick={(e) => { e.stopPropagation(); onEdit(m); }}
            className="flex h-6 w-6 items-center justify-center rounded bg-white/95 text-slate-700 shadow hover:text-blue-600"><Pencil className="h-3 w-3" /></button>
          <button type="button" data-testid={`delete-finding-media-${m.id}`} onClick={(e) => { e.stopPropagation(); onDelete(m.id); }}
            className="flex h-6 w-6 items-center justify-center rounded bg-white/95 text-slate-700 shadow hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3">
      <div className="flex flex-wrap items-start gap-6">
        <div>
          <div className="mb-1.5 inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">Before</div>
          <div className="flex flex-wrap gap-1.5">
            {before.map((m) => <Thumb key={m.id} m={m} tone="before" />)}
            {before.length === 0 && <span className="text-xs italic text-slate-400">No before photos</span>}
          </div>
        </div>
        <div>
          <div className="mb-1.5 inline-flex items-center rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">After · Repaired</div>
          <div className="flex flex-wrap gap-1.5">
            {after.map((m) => <Thumb key={m.id} m={m} tone="after" />)}
            {after.length === 0 && <span className="text-xs italic text-slate-400">No after photos</span>}
          </div>
        </div>
      </div>
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <input ref={beforeRef} type="file" accept="image/*" className="hidden" data-testid={`add-before-input-${f.id}`} onChange={(e) => { onAddBefore(e.target.files[0]); e.target.value = ""; }} />
          <input ref={afterRef} type="file" accept="image/*" className="hidden" data-testid={`add-after-input-${f.id}`} onChange={(e) => { onAddAfter(e.target.files[0]); e.target.value = ""; }} />
          <button type="button" data-testid={`add-before-btn-${f.id}`} onClick={() => beforeRef.current?.click()}
            className="flex items-center gap-1 rounded-lg border border-dashed border-blue-400 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"><Camera className="h-3.5 w-3.5" /> Add Before Photo</button>
          <button type="button" data-testid={`add-after-btn-${f.id}`} onClick={() => afterRef.current?.click()}
            className="flex items-center gap-1 rounded-lg border border-dashed border-emerald-400 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"><Camera className="h-3.5 w-3.5" /> Add After Photo</button>
        </div>
      )}
    </div>
  );
}

const EMPTY = { title: "", remarks: "", category: "Damage", severity: "minor", status: "open", location: "", cost_estimate: null, media_ids: [] };

function MediaTile({ m, canEdit, onDelete, onEdit, onEnlarge }) {
  return (
    <div className="group relative">
      {m.kind === "video"
        ? <video data-testid={`media-video-${m.id}`} src={mediaUrl(m.id)} controls className="aspect-square w-full rounded-lg bg-black object-cover" />
        : <button type="button" onClick={() => onEnlarge(m)} className="block w-full">
            <img data-testid={`media-photo-${m.id}`} src={mediaUrl(m.id)} alt={m.caption || "media"} className="aspect-square w-full cursor-zoom-in rounded-lg object-cover ring-1 ring-slate-200" />
          </button>}
      {canEdit && (
        <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
          <button data-testid={`edit-media-${m.id}`} onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit caption"
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white/95 text-slate-700 shadow hover:bg-white hover:text-blue-600"><Pencil className="h-3.5 w-3.5" /></button>
          <button data-testid={`delete-media-${m.id}`} onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete"
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white/95 text-slate-700 shadow hover:bg-white hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {m.caption ? <div className="mt-1 truncate text-xs text-slate-500" title={m.caption}>{m.caption}</div> : null}
    </div>
  );
}

export default function Inspection() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [insp, setInsp] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: EMPTY });
  const [genLoading, setGenLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [queued, setQueued] = useState(0);
  const [captionEdit, setCaptionEdit] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const canEdit = user?.role !== "viewer";
  const notesDict = useDictation((t) => setNotes((p) => (p ? p + " " : "") + t));

  const load = useCallback(() => {
    api.get(`/inspections/${id}`).then((r) => {
      setInsp(r.data);
      setSummary(r.data.ai_summary || "");
      setNotes(r.data.general_notes || "");
    }).catch(() => toast.error("Failed to load inspection"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const refreshQueue = useCallback(async () => setQueued(await queueCount()), []);
  useEffect(() => {
    const doFlush = async () => {
      const n = await flushQueue();
      await refreshQueue();
      if (n > 0) { toast.success(`Uploaded ${n} queued capture${n > 1 ? "s" : ""}`); load(); }
    };
    refreshQueue();
    window.addEventListener("online", doFlush);
    const iv = setInterval(doFlush, 20000);
    doFlush();
    return () => { window.removeEventListener("online", doFlush); clearInterval(iv); };
  }, [refreshQueue, load]);

  const saveNotes = async () => {
    await api.patch(`/inspections/${id}`, { general_notes: notes });
    toast.success("Notes saved");
  };

  const upload = async (file, kind) => {
    if (!file) return;
    const queueIt = async () => {
      await enqueue({ url: `/inspections/${id}/media`, filename: file.name, blob: file, fields: { kind } });
      await refreshQueue();
      toast.message("Saved offline", { description: "Will upload automatically when you're back online." });
    };
    if (!navigator.onLine) { await queueIt(); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/inspections/${id}/media`, fd);
      toast.success(`${kind} uploaded`);
      load();
    } catch (err) {
      await queueIt();
    } finally { setUploading(false); }
  };

  const uploadAfter = async (findingId, file) => {
    if (!file) return;
    const queueIt = async () => {
      await enqueue({ url: `/findings/${findingId}/media`, filename: file.name, blob: file, fields: { phase: "after" } });
      await refreshQueue();
      toast.message("Saved offline", { description: "After photo will upload when you're back online." });
    };
    if (!navigator.onLine) { await queueIt(); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("phase", "after");
      await api.post(`/findings/${findingId}/media`, fd);
      toast.success("After photo added");
      load();
    } catch { await queueIt(); }
  };

  const uploadBefore = async (findingId, file) => {
    if (!file) return;
    const queueIt = async () => {
      await enqueue({ url: `/findings/${findingId}/media`, filename: file.name, blob: file, fields: { phase: "before" } });
      await refreshQueue();
      toast.message("Saved offline", { description: "Before photo will upload when you're back online." });
    };
    if (!navigator.onLine) { await queueIt(); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("phase", "before");
      await api.post(`/findings/${findingId}/media`, fd);
      toast.success("Before photo added");
      load();
    } catch { await queueIt(); }
  };

  const saveFinding = async (f) => {
    try {
      const payload = { ...f, cost_estimate: f.cost_estimate === "" ? null : f.cost_estimate };
      if (f.id) await api.patch(`/findings/${f.id}`, payload);
      else await api.post(`/inspections/${id}/findings`, payload);
      setDialog({ open: false, initial: EMPTY });
      toast.success("Finding saved");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const deleteFinding = async (fid) => {
    if (!window.confirm("Delete this finding?")) return;
    await api.delete(`/findings/${fid}`);
    load();
  };

  const deleteMedia = async (mid) => {
    if (!window.confirm("Delete this media item?")) return;
    try {
      await api.delete(`/media/${mid}`);
      toast.success("Media deleted");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const saveCaption = async () => {
    if (!captionEdit) return;
    try {
      await api.patch(`/media/${captionEdit.id}`, { caption: captionEdit.caption || "" });
      toast.success("Caption saved");
      setCaptionEdit(null);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const generate = async () => {
    setGenLoading(true);
    try {
      if (notes !== insp.general_notes) await api.patch(`/inspections/${id}`, { general_notes: notes });
      const { data } = await api.post(`/inspections/${id}/generate-summary`);
      setSummary(data.ai_summary);
      toast.success("AI draft generated");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setGenLoading(false); }
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const sendEmail = async () => {
    setEmailing(true);
    try {
      await api.post(`/inspections/${id}/email`, { recipient });
      toast.success(`Report emailed to ${recipient}`);
      setEmailOpen(false);
      setRecipient("");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setEmailing(false); }
  };

  if (!insp) return <div className="py-20 text-center text-slate-400">Loading…</div>;

  const totalCost = (insp.findings || []).reduce((s, f) => s + (f.cost_estimate || 0), 0);
  const photos = (insp.media || []).filter((m) => m.kind === "photo" && !m.finding_id);
  const videos = (insp.media || []).filter((m) => m.kind === "video" && !m.finding_id);
  const findingMedia = (fid) => (insp.media || []).filter((m) => m.finding_id === fid);

  return (
    <div className="space-y-6 pb-28 lg:pb-6" data-testid="inspection-page">
      <button onClick={() => nav("/app/inspections")} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> Inspections
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-900">{insp.property_name} · Unit {insp.unit_number}</h1>
              <StatusBadge status={insp.status} />
            </div>
            <p className="mt-1 text-sm text-slate-500">{insp.property_address}</p>
            <p className="text-xs text-slate-400">Inspected {insp.inspection_date} by {insp.inspector_name}</p>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Est. repair total</div>
            <div className="font-display text-2xl font-extrabold text-blue-600">${totalCost.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {queued > 0 && (
        <div data-testid="offline-queue-banner" className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
          <UploadCloud className="h-4 w-4" /> {queued} capture{queued > 1 ? "s" : ""} queued — uploading automatically when you're back online.
        </div>
      )}

      {/* Capture bar */}
      {canEdit && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-slate-500">Capture</h2>
          <div className="grid grid-cols-3 gap-3">
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], "photo")} data-testid="photo-input" />
            <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files[0], "video")} data-testid="video-input" />
            <button data-testid="capture-photo-btn" disabled={uploading} onClick={() => photoRef.current?.click()}
              className="flex h-24 flex-col items-center justify-center gap-2 rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
              <Camera className="h-6 w-6" /> Photo
            </button>
            <button data-testid="capture-video-btn" disabled={uploading} onClick={() => videoRef.current?.click()}
              className="flex h-24 flex-col items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
              <Video className="h-6 w-6" /> Video Clip
            </button>
            <button data-testid="capture-voice-btn" onClick={notesDict.toggle}
              className={`flex h-24 flex-col items-center justify-center gap-2 rounded-xl font-semibold text-white ${notesDict.recording ? "bg-red-600 animate-pulse-subtle" : "bg-indigo-600 hover:bg-indigo-700"}`}>
              {notesDict.busy ? <Loader2 className="h-6 w-6 animate-spin" /> : notesDict.recording ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
              {notesDict.busy ? "Transcribing" : notesDict.recording ? "Stop" : "Dictate"}
            </button>
          </div>
          {uploading && <p className="mt-3 text-center text-xs text-slate-400">Uploading…</p>}
        </div>
      )}

      {/* General notes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-slate-500">Walkthrough Notes</h2>
        <textarea data-testid="general-notes-input" rows={4} readOnly={!canEdit} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Dictate or type overall notes for this unit…" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
        {canEdit && <button data-testid="save-notes-btn" onClick={saveNotes} className="mt-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200">Save notes</button>}
      </div>

      {/* Media gallery */}
      {(photos.length > 0 || videos.length > 0) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-500"><ImageIcon className="h-4 w-4" /> Media ({photos.length + videos.length})</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {[...photos, ...videos].map((m) => (
              <MediaTile key={m.id} m={m} canEdit={canEdit}
                onDelete={() => deleteMedia(m.id)}
                onEdit={() => setCaptionEdit({ id: m.id, caption: m.caption || "" })}
                onEnlarge={() => setLightbox(m)} />
            ))}
          </div>
        </div>
      )}

      {/* Findings */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-900">Findings ({insp.findings.length})</h2>
          {canEdit && (
            <button data-testid="add-finding-btn" onClick={() => setDialog({ open: true, initial: { ...EMPTY } })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
              <Plus className="h-4 w-4" /> Add
            </button>
          )}
        </div>
        <div className="divide-y divide-slate-100">
          {insp.findings.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">No findings recorded yet.</div>}
          {insp.findings.map((f) => (
            <div key={f.id} data-testid={`finding-row-${f.id}`} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{f.title}</span>
                    <SeverityBadge level={f.severity} />
                    <StatusBadge status={f.status} />
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{f.category}</span>
                  </div>
                  {f.remarks && <p className="mt-1 text-sm text-slate-600">{f.remarks}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    {f.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{f.location}</span>}
                    {f.cost_estimate ? <span className="flex items-center gap-1 font-semibold text-slate-700"><CircleDollarSign className="h-3 w-3" />${f.cost_estimate.toLocaleString()}</span> : null}
                  </div>
                  <FindingMedia f={f} media={findingMedia(f.id)} canEdit={canEdit}
                    onAddBefore={(file) => uploadBefore(f.id, file)}
                    onAddAfter={(file) => uploadAfter(f.id, file)}
                    onDelete={deleteMedia}
                    onEdit={(m) => setCaptionEdit({ id: m.id, caption: m.caption || "" })}
                    onEnlarge={(m) => setLightbox(m)} />
                </div>
                {canEdit && (
                  <div className="flex shrink-0 gap-1">
                    <button data-testid={`edit-finding-${f.id}`} onClick={() => setDialog({ open: true, initial: { ...f } })} className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-finding-${f.id}`} onClick={() => deleteFinding(f.id)} className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI report */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-tight text-slate-900"><Sparkles className="h-5 w-5 text-indigo-500" /> AI Draft Report</h2>
          {canEdit && (
            <button data-testid="generate-summary-btn" onClick={generate} disabled={genLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {genLoading ? "Generating…" : summary ? "Regenerate" : "Generate"}
            </button>
          )}
        </div>
        <textarea data-testid="summary-textarea" rows={8} readOnly={!canEdit} value={summary} onChange={(e) => setSummary(e.target.value)}
          placeholder="Tap Generate to create an AI draft summary from your notes and findings…"
          className="mt-3 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm leading-relaxed outline-none focus:border-blue-600" />
      </div>

      {/* Export actions */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <button data-testid="export-pdf-btn" onClick={() => download(`/inspections/${id}/export.pdf`, `report-unit-${insp.unit_number}.pdf`)}
          className="flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-4 font-semibold text-white hover:bg-slate-800">
          <FileText className="h-5 w-5" /> Export PDF
        </button>
        <button data-testid="export-csv-btn" onClick={() => download(`/inspections/${id}/export.csv`, `report-unit-${insp.unit_number}.csv`)}
          className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-4 font-semibold text-slate-800 hover:bg-slate-50">
          <FileSpreadsheet className="h-5 w-5" /> Export CSV
        </button>
        <button data-testid="email-report-btn" onClick={() => setEmailOpen(true)}
          className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-4 font-semibold text-white hover:bg-blue-700">
          <Mail className="h-5 w-5" /> Email Report
        </button>
      </div>

      {dialog.open && <FindingDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSave={saveFinding} initial={dialog.initial} />}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">
              {lightbox?.phase === "after" ? "After · Repaired" : lightbox?.phase === "before" ? "Before" : "Photo"}
            </DialogTitle>
          </DialogHeader>
          {lightbox && (lightbox.kind === "video"
            ? <video src={mediaUrl(lightbox.id)} controls className="max-h-[70vh] w-full rounded-lg bg-black" />
            : <img data-testid="lightbox-img" src={mediaUrl(lightbox.id)} alt={lightbox.caption || "photo"} className="max-h-[70vh] w-full rounded-lg object-contain" />)}
          {lightbox?.caption ? <p className="mt-2 text-center text-sm text-slate-500">{lightbox.caption}</p> : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!captionEdit} onOpenChange={(o) => !o && setCaptionEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display uppercase">Edit Photo Caption</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Add a short label — captions show under the photo and help identify it later.</p>
          <input data-testid="media-caption-input" value={captionEdit?.caption || ""} onChange={(e) => setCaptionEdit({ ...captionEdit, caption: e.target.value })}
            placeholder="e.g. Water heater — rear closet" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
          <DialogFooter>
            <button data-testid="save-caption-btn" onClick={saveCaption}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700">Save caption</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>        <DialogContent>
          <DialogHeader><DialogTitle className="font-display uppercase">Email Report</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Send the PDF inspection report to a property manager or bank recipient.</p>
          <input data-testid="email-recipient-input" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)}
            placeholder="recipient@bank.com" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
          <DialogFooter>
            <button data-testid="send-email-btn" disabled={!recipient || emailing} onClick={sendEmail}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
              {emailing ? "Sending…" : "Send report"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
